import {
  UNTITLED_BOARD_NAME,
  boardDefinitionTitle,
  isObjectRecord,
  type BoardRecord,
} from "@/shared/domain/boards";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "./approvalModes";
import { boardToolDeps, withDryRunContext } from "./boardAuthoringDeps";
import { ensureDryRunReport } from "./boardDryRunQuery";
import { blockedWriteError, dryRunOutcome } from "./boardDryRunResult";
import {
  boardDefinitionInputSchema,
  definitionFromCreateInput,
  describeDefinitionShapeProblem,
  USER_ASKED_FOR_NEW_BOARD,
} from "./boardToolSchema";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  ToolErrorKind,
  toolSuccess,
} from "./ClientTools";
import { CreateBoardConfirmation } from "./CreateBoardConfirmation";

// ---------------------------------------------------------------------------
// Worked example
//
// Embedded in the tool description, so the model sees one complete valid
// definition beside the schema, and exported so tests can create a board from
// it; the tool's test proves that it passes its dry run. It shows four rules:
// 1. The source's own `limit` keeps the largest groups, with no stage after it.
// 2. The stage's id and its name are different strings, and the template's
//    `stage` takes the id.
// 3. A chart a template can draw is sent as a template, with empty `bindings`.
// 4. The panel has a placement.
// The `authoring-workflow` guide chapter carries the same stage under a chart
// and a table.
// ---------------------------------------------------------------------------

export const EXAMPLE_DEFINITION = {
  schema_version: 1,
  title: "Event overview",
  time_range: "90d",
  stages: [
    {
      id: "stage-top-events",
      name: "TopEvents",
      body: {
        kind: "entity.query",
        target: "events",
        aggregate: { op: "count", groupBy: "name" },
        limit: 5,
      },
    },
  ],
  panels: [
    {
      kind: "chart",
      id: "top-events-chart",
      title: "Most common events",
      placement: { x: 0, y: 0, width: 720, height: 450 },
      bindings: [],
      template: {
        name: "builder",
        params: {
          stage: "stage-top-events",
          mark: "arc",
          encoding: {
            theta: { field: "count", type: "quantitative" },
            color: { field: "name", type: "nominal", title: "Event" },
          },
          brush: false,
          innerRadius: 60,
        },
      },
    },
  ],
} as const;

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

export type CreateBoardInput = Record<string, unknown>;

/**
 * Dry-runs a complete board definition, saves it as a personal board with each
 * chart template expanded into its spec and each builder chart given the value
 * types the dry run checked it with, and opens the saved board. The dry
 * run is the confirmation card's when the card ran one for the same
 * definition, and one the tool runs otherwise. A blocked dry run or a failed save
 * returns an error and navigates nowhere. The tool checks only the
 * definition's shape; the authoring service's dry run validates the rest.
 */
async function execute(
  input: CreateBoardInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  const definition = definitionFromCreateInput(input);
  const shapeProblem = describeDefinitionShapeProblem(definition);
  if (shapeProblem !== null) {
    return toolError(
      shapeProblem.message,
      { member: shapeProblem.member },
      ToolErrorKind.VALIDATION,
    );
  }
  const boardDeps = boardToolDeps(deps);
  const { goto, createBoard } = boardDeps;
  return withDryRunContext(boardDeps, async (service, ctx) => {
    const report = await ensureDryRunReport(definition, ctx);
    if (report.status === "blocked") {
      return blockedWriteError(
        report,
        "The definition did not pass its dry run, so nothing was created.",
        "Fix all of them and call again.",
      );
    }
    const expanded = await service.completeBuilderValueTypes(
      service.expand(definition).definition,
      ctx,
    );
    if (!isObjectRecord(expanded)) {
      return toolError(
        "The board definition, after its chart templates were expanded into specs, must be a JSON object.",
        {},
        ToolErrorKind.VALIDATION,
      );
    }
    const name = boardDefinitionTitle(expanded) ?? UNTITLED_BOARD_NAME;
    let record: BoardRecord;
    try {
      record = await createBoard({
        name,
        board_definition: expanded,
      });
    } catch (error) {
      return createFailure(error, name);
    }
    const route = Goto.createBoardRoute(record.board_id);
    goto.navigateTo(route);
    return toolSuccess("created_board", {
      board_id: record.board_id,
      navigated_to: route,
      title: record.name,
      panel_count: Array.isArray(definition.panels)
        ? definition.panels.length
        : 0,
      ...dryRunOutcome(report),
    });
  });
}

/**
 * The result for a create request that did not succeed, after a dry run that
 * passed. The platform refusing the request means nothing was created, and
 * its own sentence says why. Anything else (a dropped connection, a server
 * fault) leaves it unknown whether the board was created, and a second call
 * would create a second board, so the result says how to find out first.
 * The error's own text is left out because it describes the network or the
 * server, which the model cannot act on; the create mutation behind
 * `deps.createBoard` reports it to error monitoring.
 */
function createFailure(
  error: unknown,
  name: string,
): ClientToolExecutionResult {
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return toolError(
      `The platform refused to create the board, so nothing was created: ${error.message}`,
      {},
      ToolErrorKind.VALIDATION,
    );
  }
  return toolError(
    "The definition passed its dry run, but the request to create the board " +
      "did not complete, so the board may or may not exist. Before calling " +
      `again, call client_read_board with name_contains "${name}" and look ` +
      "for a board with that name and a changed_at of the last few minutes. " +
      "If one is there, the create succeeded: use its id. Create again only " +
      "if none is.",
    { name },
    ToolErrorKind.TRANSPORT,
  );
}

/**
 * The refusal of a create while the request it answers applies to a board
 * (`deps.requestsApplyTo`), unless the input says the user asked for a new
 * board; undefined when the create may go on. The refusal names the board and
 * the `client_update_board` call that builds or changes it.
 */
function refusalWhileRequestsApplyToABoard(
  input: Record<string, unknown>,
  deps?: ClientToolDeps,
): ClientToolExecutionResult | undefined {
  const board = deps?.requestsApplyTo;
  if (!board || input[USER_ASKED_FOR_NEW_BOARD] === true) {
    return undefined;
  }
  const { boardId } = board;
  return toolError(
    `Nothing was created. The user sent this request from board "${boardId}" ` +
      "(misc_context.requests_apply_to), so the request is about that board: " +
      "build or change it with client_update_board and board_id " +
      `"${boardId}". On a board with no stages and no panels, one call builds ` +
      "this definition: each stage as an add_stage operation, in order, and " +
      "each panel as an add operation; send title, time_range, sections or " +
      "selections only to change them, keeping what the board has. On a " +
      "board with stages or panels, read it " +
      "first with client_read_board. Call client_create_board again, with " +
      `${USER_ASKED_FOR_NEW_BOARD} set to true, only if the user asked for a ` +
      "new board.",
    { board_id: boardId },
    ToolErrorKind.VALIDATION,
  );
}

// ---------------------------------------------------------------------------
// Tool module
// ---------------------------------------------------------------------------

/** The definition schema, with the member that says the user asked for a new board. */
const createBoardInputSchema: Record<string, unknown> = {
  ...boardDefinitionInputSchema,
  properties: {
    ...(boardDefinitionInputSchema.properties as Record<string, unknown>),
    [USER_ASKED_FOR_NEW_BOARD]: {
      type: "boolean",
      enum: [true],
      description:
        "Not part of the definition. Send true only when " +
        "misc_context.requests_apply_to names a board and the user asked " +
        "for a new board: they said a new, another or separate board, or " +
        "asked for a board about something else while the board it names " +
        "has stages or panels. Omit it otherwise.",
    },
  },
};

export const tool = createClientTool<CreateBoardInput>({
  name: "client_create_board",
  description:
    "Creates one personal board from a complete definition and opens it. " +
    "While misc_context.requests_apply_to names a board, build or change " +
    "that board with client_update_board, even when it is empty; this tool " +
    `then refuses unless ${USER_ASKED_FOR_NEW_BOARD} is true. ` +
    "Returns its board_id; each panel's status and warnings with the id, " +
    "name and row count of every stage it binds, and for a chart which " +
    "views show no tooltip and why, and which legends select series; " +
    "unbound_stages, the " +
    "stages no panel binds; warnings about the board as a whole; and " +
    "resolved_time_range, the board time range the stages ran " +
    "over, as ISO 8601 start and end times in the board's time zone with " +
    "its offset. The definition is " +
    `this tool's input itself, beside ${USER_ASKED_FOR_NEW_BOARD}. It is ` +
    "dry-run against live data first: if any stage or panel fails, nothing " +
    "is created and the result carries the dry-run report " +
    "(client_dry_run_board returns the same report and creates nothing); fix " +
    "what it lists and call again. The " +
    "get_board_authoring_guide chapters carry the rules a schema cannot " +
    "express. Example of a complete valid definition: " +
    JSON.stringify(EXAMPLE_DEFINITION),
  input_schema: createBoardInputSchema,
  execute,
  refusalBeforeApproval: refusalWhileRequestsApplyToABoard,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Create board",
  displayDescription:
    "Dry-runs an AI-authored board definition against live data, then saves it as a new personal board and opens it. Never modifies an existing board.",
  ConfirmationContent: CreateBoardConfirmation,
});
